import { useMemo } from 'react'

import { useCubeReportPageFiltersContext } from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  DRIFT_CHAT_CONVERSATIONS_RATINGS_MEASURES,
  ROUTING_MEASURES,
} from 'cube/constants'
import { isNil } from 'ramda'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import {
  type RoutingKPIMetric,
  resolveCubeKPIResults,
  resolveKPIScore,
  resolveTotalRecords,
} from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

import { KPI_SCORES } from './AverageCSAT.constants'
import { AverageCSATQuery } from './AverageCSAT.query'

/***
 * Hooking that uses cube to fetch CSAT data for conversations w/ routed teammates.
 *
 * @returns {RoutingKPIMetric}
 */
export const useAverageCSAT = (): RoutingKPIMetric => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()

  const cubeQuery = new CubeQueryBuilder({ query: AverageCSATQuery })
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withFilters(pageFilters)
    .build()

  const { data, isLoading, isError } = useCubeQuery({
    query: cubeQuery,
  })

  const averageCSAT = useMemo(
    () =>
      resolveCubeKPIResults(
        data,
        DRIFT_CHAT_CONVERSATIONS_RATINGS_MEASURES.RATING_AVERAGE
      ),
    [data]
  )

  const totalRecords = useMemo(
    () =>
      resolveTotalRecords(
        data,
        DRIFT_CHAT_CONVERSATIONS_RATINGS_MEASURES.RATING_COUNT
      ),
    [data]
  )

  const totalRequests = useMemo(
    () => resolveTotalRecords(data, ROUTING_MEASURES.ROUTING_COUNT),
    [data]
  )

  const score = useMemo(
    () => resolveKPIScore(averageCSAT, KPI_SCORES),
    [averageCSAT]
  )

  const metric = useMemo(() => averageCSAT ?? null, [averageCSAT])

  const hasNoData = useMemo(
    () => !isLoading && isNil(averageCSAT),
    [isLoading, averageCSAT]
  )

  return useMemo(
    () => ({
      score,
      metric,
      isLoading,
      isError,
      hasNoData,
      totalRecords,
      totalRequests,
    }),
    [score, metric, isLoading, isError, hasNoData, totalRecords, totalRequests]
  )
}

export default useAverageCSAT
